import { ChartDataResponse, KnownMetricCodes, MetricInfo, MetricLabels } from "@api/common";
import { AdUnitFormat, AdUnitType } from "@store";
import * as cn from "classnames";
import * as d3 from "d3";
import * as React from "react";
import { connect } from "react-redux";
import "./AdTypeDetails.css";
import { ProgressChart } from "./ProgressChart";
import { ReportLabel } from "./ReportLabel";
import { formatNumber } from "@common";

interface AdTypeDetailsComponentProps {
    adType: AdUnitType;

    metrics: AdTypeMetricsInfo[];
    interactions: InteractionInfo[];

    className?: string;
}

interface InteractionInfo {
    interaction: string;
    title: string;
    rate: number;
}

interface AdTypeMetricsInfo {
    metric: MetricInfo;
    value: number;
}

function AdTypeDetailsComponent({ adType, metrics, interactions, className }: AdTypeDetailsComponentProps) {
    const colorScale = d3
        .scaleLinear()
        .domain([interactions.length, 0])
        .range([0.1, 1]);

    const interactionColor = (index: number): string => {
        const color = d3.color("#1A7FB3" /*"#1a8bb3"*/);
        const opacity = colorScale(index);
        color.opacity = opacity;

        return color.toString();
    };

    return (
        <div className={cn("AdTypeDetails", className)}>
            <div className="AdTypeDetails__header">
                <img className="AdTypeDetails__header-icon" src={adType.reportingIcon} />
                <ReportLabel title={adType.name} />
            </div>
            <div className="AdTypeDetails__metrics">
                {metrics.map(m => (
                    <div className="AdTypeDetailsMetric" key={m.metric.metric}>
                        <div className="AdTypeDetailsMetric__title">{MetricLabels.metricSwitcher(m.metric)}</div>
                        <div className="AdTypeDetailsMetric__value">{formatNumber(m.value)}</div>
                    </div>
                ))}
            </div>
            <div className="AdTypeDetails__interactions">
                {interactions.map((interaction, index) => (
                    <div className="AdTypeDetailsInteraction" key={interaction.interaction}>
                        <div className="AdTypeDetailsInteraction__title">{interaction.title}</div>
                        <ProgressChart
                            className="AdTypeDetailsInteraction__progress"
                            progress={interaction.rate * 100}
                            progressColor={interactionColor(index)}
                        />
                        <div className="AdTypeDetailsInteraction__percent">{`${(interaction.rate * 100).toFixed(
                            2
                        )}%`}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export interface AdTypeDetailsProps {
    adFormat: AdUnitFormat;
    adType: AdUnitType;
    metrics: MetricInfo[];

    data: ChartDataResponse;
    className?: string;
}

export const AdTypeDetails = connect(
    (_, ownProps: AdTypeDetailsProps): AdTypeDetailsComponentProps => ({
        ...createAdTypeDetails(ownProps.data, ownProps.adType, ownProps.adFormat, ownProps.metrics),
        className: ownProps.className
    })
)(AdTypeDetailsComponent);

function createAdTypeDetails(
    data: ChartDataResponse,
    adType: AdUnitType,
    adFormat: AdUnitFormat,
    metrics: MetricInfo[]
): AdTypeDetailsComponentProps {
    const interactions = adType.interaction.actions
        .map<InteractionInfo>(a => ({
            interaction: a.type,
            title: a.name,
            rate: findMetric(data, `rate(${KnownMetricCodes.impression},${a.type})`, adType, adFormat)
        }))
        .filter(a => a.rate !== undefined);

    return {
        adType,
        metrics: metrics.map<AdTypeMetricsInfo>(metric => ({
            metric,
            value: findMetric(data, metric.metric, adType, adFormat) || 0
        })),
        interactions
    };
}

function findMetric(
    data: ChartDataResponse,
    metric: string,
    adType: AdUnitType,
    adFormat: AdUnitFormat
): number | undefined {
    const metricData = data.metrics.find(m => m.metric === metric);
    if (!metricData) {
        return undefined;
    }

    const value = metricData.data.find(
        d =>
            d["dimension-values"].indexOf(adFormat.size) !== -1 &&
            d["dimension-values"].indexOf(adType.interaction.name) !== -1
    );

    if (!value) {
        return undefined;
    }

    return value["metric-value"];
}
